import { createAsyncThunk } from "@reduxjs/toolkit"

import * as services from "../api/services"
import { ANALYTICS_SECTION, ANALYTICS_SLICE_NAME as SLICE_NAME } from "./analytics.constants"

// One thunk per endpoint. Each takes `{ key, params }`: the endpoint is the thunk, the store key
// is the caller's, which is how a card and its full page read the same endpoint into different
// keys (see ANALYTICS_SECTION).
//
// Silent by design (no handleApiError): every section renders its own empty state, and a merchant
// who has not connected a processor yet would otherwise get a global error toast per section.

export const getAnalyticsQueue = createAsyncThunk(
  `${SLICE_NAME}/get_queue`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsQueue(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsKpis = createAsyncThunk(
  `${SLICE_NAME}/get_kpis`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsKpis(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsTrend = createAsyncThunk(
  `${SLICE_NAME}/get_trend`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsTrend(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsFlow = createAsyncThunk(
  `${SLICE_NAME}/get_flow`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsFlow(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsByCategory = createAsyncThunk(
  `${SLICE_NAME}/get_by_category`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsByCategory(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsByCountry = createAsyncThunk(
  `${SLICE_NAME}/get_by_country`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsByCountry(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsByCardNetwork = createAsyncThunk(
  `${SLICE_NAME}/get_by_card_network`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsByCardNetwork(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsWinRateMatrix = createAsyncThunk(
  `${SLICE_NAME}/get_win_rate_matrix`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsWinRateMatrix(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsRepeatDisputers = createAsyncThunk(
  `${SLICE_NAME}/get_repeat_disputers`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsRepeatDisputers(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsCategoryTrends = createAsyncThunk(
  `${SLICE_NAME}/get_category_trends`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsCategoryTrends(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

export const getAnalyticsActivity = createAsyncThunk(
  `${SLICE_NAME}/get_activity`,
  async ({ params }, { rejectWithValue }) => {
    const response = await services.getAnalyticsActivity(params)
    if (!response.ok) return rejectWithValue(response.data ?? null)
    return response.data
  }
)

// Store key -> the endpoint behind it.
export const SECTION_THUNKS = {
  [ANALYTICS_SECTION.KPIS]: getAnalyticsKpis,
  [ANALYTICS_SECTION.TREND]: getAnalyticsTrend,
  [ANALYTICS_SECTION.QUEUE]: getAnalyticsQueue,
  [ANALYTICS_SECTION.FLOW]: getAnalyticsFlow,
  [ANALYTICS_SECTION.BY_CATEGORY]: getAnalyticsByCategory,
  [ANALYTICS_SECTION.BY_COUNTRY]: getAnalyticsByCountry,
  [ANALYTICS_SECTION.BY_CARD_NETWORK]: getAnalyticsByCardNetwork,
  [ANALYTICS_SECTION.WIN_RATE_MATRIX]: getAnalyticsWinRateMatrix,
  [ANALYTICS_SECTION.REPEAT_DISPUTERS]: getAnalyticsRepeatDisputers,
  [ANALYTICS_SECTION.REPEAT_DISPUTERS_PAGE]: getAnalyticsRepeatDisputers,
  [ANALYTICS_SECTION.CATEGORY_TRENDS]: getAnalyticsCategoryTrends,
  [ANALYTICS_SECTION.REASON_CODES_TREND]: getAnalyticsCategoryTrends,
  [ANALYTICS_SECTION.ACTIVITY]: getAnalyticsActivity,
  [ANALYTICS_SECTION.ACTIVITY_PAGE]: getAnalyticsActivity
}
